// AppRouter — the ONE route tree (docs/ONE_APP.md).
//
// Replaces RebuildRouter's three-way split into /field, /dispatch and /office,
// each with its own shell. There are no app prefixes any more: a path names a
// CAPABILITY, and whether a person can reach it is a property of their role.
//
// Every path here has exactly one entry in apps/shared/navigation.js, and
// oneApp.test.js asserts that in both directions — a route with no destination
// is unreachable except by typing the URL, and a destination with no route is a
// menu row that 404s. Both have shipped in this repo before.
//
// ── Guarding ────────────────────────────────────────────────────────────────
//
// `Allowed` checks the same `roles` list the menu is built from, so a person
// who types a URL they were not offered gets the same answer as if they had
// looked for it: it is not there. It is NOT the security boundary — the server
// checks every request independently, and permissions.js is the authority. This
// only decides what the UI offers, which is why it errs toward showing a
// refusal on arrival rather than duplicating the permission model.

import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { EmptyState } from '../ui/fw';
import { useAuth } from '../App';
import { useRole } from '../hooks/usePermission';
import { usePhoneLayout } from '../ui/fw/usePhoneLayout';
import { destinationsFor, homePathFor, keyForPath } from './shared/navigation';
import { LEGACY_PATHS, resolveLegacyPath } from './shared/legacyPaths';
import AppShell from './shell/AppShell';

// the day
import Today from './field/screens/Today';
import Job from './field/screens/Job';
import Hours from './field/screens/Hours';
import Problem from './field/screens/Problem';
import TimeOff from './field/screens/TimeOff';
// dispatch
import Board from './dispatch/screens/Board';
import Now from './dispatch/screens/Now';
import Approve from './dispatch/screens/Approve';
import SheetDetail from './dispatch/screens/SheetDetail';
// work + money + records + insight + admin
import Overview from './office/screens/Overview';
import Jobs from './office/records/Jobs';
import Schedule from './office/screens/Schedule';
import Invoices from './office/screens/Invoices';
import InvoiceDetail from './office/screens/InvoiceDetail';
import InvoiceFromJobs from './office/screens/InvoiceFromJobs';
import Estimates from './office/screens/Estimates';
import EstimateDetail from './office/screens/EstimateDetail';
import Payroll from './office/screens/Payroll';
import MoneyInOut from './office/screens/MoneyInOut';
import Expenses from './office/screens/Expenses';
import SalesTax from './office/screens/SalesTax';
import Form1099s from './office/screens/Form1099s';
import Clients from './office/records/Clients';
import Properties from './office/records/Properties';
import Crew from './office/records/Crew';
import Reports from './office/insight/Reports';
import Notifications from './office/insight/Notifications';
import Media from './office/insight/Media';
import Activity from './office/insight/Activity';
import EmailLog, { SystemStatus } from './office/admin/EmailLog';
import Settings from './office/admin/Settings';
import Users from './office/admin/Users';
import Security from './office/admin/Security';
import Roles from './office/admin/Roles';
import Me from './shell/Me';

/**
 * Refuse a destination this role was never offered.
 *
 * A sentence, not a redirect. Bouncing someone to the home page for typing a
 * URL is the shape where a person cannot tell "I am not allowed" from "the app
 * is broken", and this repo has spent the year removing exactly that ambiguity.
 */
function Allowed({ navKey, children }) {
  const role = useRole();
  const { user } = useAuth();
  // Reads the same permissions the menu does, deliberately. If this asked only
  // the role label while the sidebar asked permissions, a granted person would
  // see a row that answered "Not part of your app" when they tapped it — the
  // ghost-button failure in the other direction.
  const ok = destinationsFor(role, { permissions: user && user.permissions })
    .some((d) => d.key === navKey);
  if (!ok) {
    return (
      <EmptyState icon="lock" title="Not part of your app">
        This screen is not one your role uses. If you think it should be, ask an admin.
      </EmptyState>
    );
  }
  return children;
}

const R = (navKey, element) => <Allowed navKey={navKey}>{element}</Allowed>;

function Home() {
  const role = useRole();
  const phone = usePhoneLayout();
  const { user } = useAuth();
  return <Navigate to={homePathFor(role, { phone, permissions: user && user.permissions })} replace />;
}

/** A path nothing claims. Says so, rather than bouncing somewhere. */
function NotFound() {
  const location = useLocation();
  const known = keyForPath(location.pathname);
  if (known) return <Home />;
  return (
    <EmptyState icon="search" title="No such screen">
      Nothing lives at {location.pathname}. Use the menu to get back.
    </EmptyState>
  );
}

/**
 * A path this app has stopped serving, arriving anyway.
 *
 * The server still writes classic paths into `notifications.link` — every
 * timesheet approve/reject sends `/timesheets`, every crew problem report
 * sends `/dispatch` — and the Notifications screen renders each as an "Open"
 * link. RebuildRouter used to redirect them and was deleted with the classic
 * app, so since then those two, the app's highest-frequency notifications,
 * have opened onto the not-found screen. shared/legacyPaths.js carries the
 * table and the evidence for every row.
 *
 * `reachable` is asked with the SAME destinations `Allowed` and the menu read,
 * because one old path serves two roles: '/timesheets' is Approvals for the
 * manager who was told a sheet was submitted, and My hours for the worker who
 * was told theirs was approved. Sending the worker to Approvals would answer
 * their own notification with "Not part of your app".
 */
function LegacyRedirect() {
  const location = useLocation();
  const role = useRole();
  const { user } = useAuth();
  const mine = destinationsFor(role, { permissions: user && user.permissions });
  const here = `${location.pathname}${location.search}${location.hash}`;
  const to = resolveLegacyPath(here, { reachable: (p) => mine.some((d) => d.path === p) });
  // A row that resolves to itself would loop. Unreachable while every entry
  // maps somewhere else — legacyPaths.test.js pins that — and cheaper to
  // refuse than to discover as a hung tab.
  if (!to || to === here) return <NotFound />;
  return <Navigate to={to} replace />;
}

export default function AppRouter() {
  const { user, loading } = useAuth();
  if (loading || !user) return null;
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Home />} />

        {/* the day — everyone */}
        <Route path="/today" element={R('today', <Today />)} />
        <Route path="/today/job/:id" element={R('today', <Job />)} />
        <Route path="/hours" element={R('hours', <Hours />)} />
        <Route path="/problem" element={R('problem', <Problem />)} />
        <Route path="/time-off" element={R('time-off', <TimeOff />)} />

        {/* dispatch */}
        <Route path="/board" element={R('board', <Board />)} />
        <Route path="/now" element={R('now', <Now />)} />
        <Route path="/approve" element={R('approve', <Approve />)} />
        <Route path="/approve/:id" element={R('approve', <SheetDetail />)} />

        {/* work */}
        <Route path="/overview" element={R('overview', <Overview />)} />
        <Route path="/jobs" element={R('jobs', <Jobs />)} />
        <Route path="/schedule" element={R('schedule', <Schedule />)} />

        {/* money */}
        <Route path="/invoices" element={R('invoices', <Invoices />)} />
        <Route path="/invoices/new" element={R('invoices', <InvoiceDetail />)} />
        <Route path="/invoices/from-jobs" element={R('invoices', <InvoiceFromJobs />)} />
        <Route path="/invoices/:id" element={R('invoices', <InvoiceDetail />)} />
        <Route path="/estimates" element={R('estimates', <Estimates />)} />
        <Route path="/estimates/new" element={R('estimates', <EstimateDetail />)} />
        <Route path="/estimates/:id" element={R('estimates', <EstimateDetail />)} />
        <Route path="/payroll" element={R('payroll', <Payroll />)} />
        <Route path="/money" element={R('money', <MoneyInOut />)} />
        <Route path="/expenses" element={R('expenses', <Expenses />)} />
        <Route path="/taxes" element={R('taxes', <SalesTax />)} />
        <Route path="/1099s" element={R('nec', <Form1099s />)} />

        {/* records */}
        <Route path="/clients" element={R('clients', <Clients />)} />
        <Route path="/properties" element={R('properties', <Properties />)} />
        <Route path="/crew" element={R('crew', <Crew />)} />

        {/* insight */}
        <Route path="/reports" element={R('reports', <Reports />)} />
        <Route path="/notifications" element={R('notifications', <Notifications />)} />
        <Route path="/media" element={R('media', <Media />)} />
        <Route path="/activity" element={R('activity', <Activity />)} />

        {/* admin */}
        <Route path="/settings" element={R('settings', <Settings />)} />
        <Route path="/users" element={R('users', <Users />)} />
        <Route path="/security" element={R('security', <Security />)} />
        <Route path="/roles" element={R('roles', <Roles />)} />
        <Route path="/email-log" element={R('email-log', <EmailLog />)} />
        <Route path="/system" element={R('system', <SystemStatus />)} />

        {/* everyone */}
        <Route path="/me" element={R('me', <Me />)} />

        {/* old paths a notification or a bookmark still carries. Built from
            the table so a `path=` literal never names one — they are not
            destinations, and oneApp.test.js reads this file for both. */}
        {LEGACY_PATHS.map((e) => (
          <Route key={e.from} path={`${e.from}/*`} element={<LegacyRedirect />} />
        ))}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  );
}
